import * as React from "react";
import { DataProvider, useDataProvider, useTranslate } from "react-admin";
import CreatorPage from "../creatorsShared/CreatorPage";
import { createWithId } from "../creatorsShared/createWithId";
import { updateRecord } from "../creatorsShared/updateRecord";
import MainPageCreatorBuilderForm from "./MainPageCreatorBuilderForm";
import MainPageCreatorRender from "./MainPageCreatorRender";
import { MainPage, WithId } from "../../types";

interface EditingIds {
  mainPageId: string;
  bannerId: string;
  aboutId: string;
  benefitsId: string;
  popularId: string;
}

const emptyDefaults = {
  banner: {},
  about: {},
  benefits: {},
  popular: {},
};

// Upserts one sub-record: updates it in place when editingId is known,
// otherwise creates it and returns the new id (see createWithId for why
// dataProvider.create's own returned id can't be trusted here).
const upsert = async (
  dataProvider: DataProvider,
  resource: string,
  editingId: string | undefined,
  payload: Record<string, any>
): Promise<string> => {
  if (editingId) {
    await updateRecord(dataProvider, resource, editingId, payload);
    return editingId;
  }
  return createWithId(resource, payload);
};

const MainPageCreatorPage = () => {
  const dataProvider = useDataProvider();
  const translate = useTranslate();

  const loadRecord = async (option: WithId) => {
    const { data: mainPage } = await dataProvider.getOne<MainPage>("mainPage", {
      id: option.id,
    });
    // Same dataProvider.getOne id-clobbering quirk as the services
    // creator - keep the ids we already know instead of trusting the
    // fetched records' "id" field. See PreviewPage.tsx for the full note.
    const ids: EditingIds = {
      mainPageId: option.id,
      bannerId: mainPage.banner,
      aboutId: mainPage.about,
      benefitsId: mainPage.benefits,
      popularId: mainPage.popular,
    };

    const [{ data: banner }, { data: about }, { data: benefits }, { data: popular }] =
      await Promise.all([
        dataProvider.getOne("banners", { id: ids.bannerId }),
        dataProvider.getOne("about", { id: ids.aboutId }),
        dataProvider.getOne("benefits", { id: ids.benefitsId }),
        dataProvider.getOne("popular", { id: ids.popularId }),
      ]);

    return {
      defaultValues: {
        banner: { ...banner },
        about: { ...about },
        benefits: { ...benefits },
        popular: { ...popular },
      },
      editingIds: ids,
    };
  };

  const saveRecord = async (
    values: Record<string, any>,
    editingIds: EditingIds | null
  ) => {
    const bannerId = await upsert(dataProvider, "banners", editingIds?.bannerId, {
      bannerImage: values.banner?.bannerImage,
      bannerTitle: values.banner?.bannerTitle,
      bannerSubtitle: values.banner?.bannerSubtitle,
      bannerSlogans: values.banner?.bannerSlogans,
    });

    const aboutId = await upsert(dataProvider, "about", editingIds?.aboutId, {
      sectionImage: values.about?.sectionImage,
      sectionTitle: values.about?.sectionTitle,
      sectionSubtitle: values.about?.sectionSubtitle,
      sectionDescription: values.about?.sectionDescription,
      companyInfo: values.about?.companyInfo,
    });

    const benefitsId = await upsert(dataProvider, "benefits", editingIds?.benefitsId, {
      sectionTitle: values.benefits?.sectionTitle,
      sectionSubtitle: values.benefits?.sectionSubtitle,
      sectionDescription: values.benefits?.sectionDescription,
      benefits: values.benefits?.benefits,
    });

    const popularId = await upsert(dataProvider, "popular", editingIds?.popularId, {
      sectionTitle: values.popular?.sectionTitle,
      sectionSubtitle: values.popular?.sectionSubtitle,
      sectionDescription: values.popular?.sectionDescription,
      services: values.popular?.services,
    });

    const mainPagePayload = {
      banner: bannerId,
      about: aboutId,
      benefits: benefitsId,
      popular: popularId,
    };

    let mainPageId: string;
    if (editingIds) {
      await updateRecord(dataProvider, "mainPage", editingIds.mainPageId, mainPagePayload);
      mainPageId = editingIds.mainPageId;
    } else {
      mainPageId = await createWithId("mainPage", mainPagePayload);
    }

    return { resource: "mainPage", id: mainPageId };
  };

  return (
    <CreatorPage<WithId, EditingIds>
      titleKey="mainPageCreator.title"
      subtitleKey="mainPageCreator.subtitle"
      saveKey="mainPageCreator.save"
      saveExistingKey="mainPageCreator.saveExisting"
      successKey="mainPageCreator.notifications.success"
      errorKey="mainPageCreator.notifications.error"
      loadErrorKey="mainPageCreator.notifications.loadError"
      selector={{
        resource: "mainPage",
        getOptionLabel: (option) => `${translate("mainPage.name")} (${option.id})`,
        labelKey: "mainPageCreator.selector.label",
        placeholderKey: "mainPageCreator.selector.new",
      }}
      emptyDefaults={emptyDefaults}
      loadRecord={loadRecord}
      saveRecord={saveRecord}
      BuilderForm={MainPageCreatorBuilderForm}
      RenderPreview={MainPageCreatorRender}
    />
  );
};

export default MainPageCreatorPage;
